import { Option } from "components/SingleSelect/types";
import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import { useGetCountriesQuery, useGetSocialPlatformsQuery } from "slice/common";
import {
    useGetProfileStrengthQuery,
    useGetSocialLinksQuery,
    useGetUserQuery,
    useSetPersonalDetailsMutation,
    useSetSocialLinksMutation,
} from "slice/user";
import { validations } from "utils/validations";
import { getPersonalDetailsErrors, getSocialLinkWithErrors } from "./helpers";
import { IPersonalDetails, SocialResponse } from "./types";

export const usePersonalDetails = () => {
    const { user_id } = useAppSelector((state) => state.userAuth),
        { data: countries } = useGetCountriesQuery({}),
        { data: socialPlatforms = [] } = useGetSocialPlatformsQuery({}),
        { data: socialHandleData, isFetching: isSocialFetching } =
            useGetSocialLinksQuery(user_id, {
                refetchOnMountOrArgChange: true,
            }),
        [
            setSocialLinks,
            {
                data: updateSocialDetailsResponse,
                isLoading: isSocialLoading,
                isSuccess: isSetSocialsSuccess,
            },
        ] = useSetSocialLinksMutation(),
        {
            data: personalDetailsResponse,
            isFetching: isPersonalDetailsFetching,
        } = useGetUserQuery(user_id, {
            refetchOnMountOrArgChange: true,
        }),
        [
            setDetails,
            {
                data: updatePersonalDetailsResponse,
                isLoading: isPersonalDetailLoading,
                isSuccess: isSetDetailsSuccess,
            },
        ] = useSetPersonalDetailsMutation(),
        [nationalityData, setNationalityData] = useState<Option[]>([]),
        [socialHandles, setSocialHandles] = useState<SocialResponse[]>([
            {
                id: String(new Date().getTime()),
                username: "",
                social_platforms_id: 1,
            },
        ]);
    const { data, refetch } = useGetProfileStrengthQuery(user_id ?? "", {
        skip: !user_id,
    });
    const dispatch = useDispatch(),
        navigateTo = useNavigate(),
        state = useLocation().state as { showToast: boolean };

    const [personalDetails, setPersonalDetails] = useState<IPersonalDetails>({
            first_name: "",
            last_name: "",
            gender: "",
            nationality: "",
            qualification: "",
            dob: "",
        }),
        [personalDetailsErrors, setPersonalDetailsErrors] =
            useState<IPersonalDetails>({
                first_name: "",
                last_name: "",
                gender: "",
                nationality: "",
                qualification: "",
                dob: "",
            }),
        [socialHttpMethod, setSocialHttpMethod] = useState("POST"),
        [personalDetailsHttpMethod, setPersonalDetailsHttpMethod] =
            useState("POST"),
        [clickedOnNext, setClickedOnNext] = useState(false);

    const getSocialPlatformOptions = useCallback(
        (socialPlatforms: Array<SocialResponse>) =>
            socialPlatforms.map((socialPlatform: any) => ({
                value: socialPlatform.id,
                label: socialPlatform.name,
            })),
        []
    );
    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target;
            setPersonalDetails((prevState) => ({
                ...prevState,
                [name]: value,
            }));

            setPersonalDetailsErrors((prevState) => ({
                ...prevState,
                [name]: validations(name, value),
            }));
        },
        []
    );

    const handleSelectChange = useCallback((name: string, value: string) => {
        setPersonalDetails((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        setPersonalDetailsErrors((prevState) => ({
            ...prevState,
            [name]: validations(name, value),
        }));
    }, []);

    const onPersonalDetailsSubmit = useCallback(() => {
        const { errors, hasError } = getPersonalDetailsErrors(personalDetails);

        if (hasError) {
            setPersonalDetailsErrors(errors);
        } else {
            setDetails({
                user_id,
                ...personalDetails,
                method: personalDetailsHttpMethod,
            }).then(() => {
                refetch();
            });
        }
    }, [personalDetails, personalDetailsHttpMethod, setDetails, user_id]);

    const handleSocialSelect = useCallback((value: number, id: string) => {
        setSocialHandles((prevState) =>
            prevState.map((prev) =>
                prev.id === id
                    ? {
                          ...prev,
                          social_platforms_id: value,
                      }
                    : prev
            )
        );
    }, []);

    const handleSocialUsername = useCallback((event: any, id: string) => {
        const { value } = event.target;
        setSocialHandles((prevState) => [
            ...prevState.map((prev) =>
                prev.id === id
                    ? {
                          ...prev,
                          username: value,
                          error: validations("socialUserName", value),
                      }
                    : prev
            ),
        ]);
    }, []);

    const addSocialHandle = useCallback(() => {
        setSocialHandles((prevState) => [
            ...prevState,
            {
                id: String(new Date().getTime()),
                username: "",
                social_platforms_id: 1,
            },
        ]);
    }, []);

    const removeSocialHandle = useCallback((value: string) => {
        setSocialHandles((prevState) =>
            prevState.filter((socialHandle) => socialHandle.id !== value)
        );
    }, []);

    const handleSocialLinksSubmit = useCallback(() => {
        const { hasError, updatedLinks } =
            getSocialLinkWithErrors(socialHandles);

        if (hasError) {
            setSocialHandles(updatedLinks);
        } else {
            setSocialLinks({
                socialHandles,
                method: socialHttpMethod,
                user_id,
            }).then(() => {
                refetch();
            });
        }
    }, [setSocialLinks, socialHandles, socialHttpMethod, user_id]);

    const handleSocialEditButtonClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            setSocialHttpMethod("PUT");
        },
        []
    );

    const handlePersonalDetailsEditButtonClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            setPersonalDetailsHttpMethod("PUT");
        },
        []
    );

    const handleSaveAndNext = useCallback(() => {
        const { hasError: hasSocialLinksError, updatedLinks } =
            getSocialLinkWithErrors(socialHandles);

        const { errors, hasError: hasPersonalDetailsError } =
            getPersonalDetailsErrors(personalDetails);

        if (!hasSocialLinksError && !hasPersonalDetailsError) {
            setClickedOnNext(true);
            setDetails({
                user_id,
                ...personalDetails,
                method: personalDetailsHttpMethod,
            });
            setSocialLinks({
                socialHandles,
                method: socialHttpMethod,
                user_id,
            }).then(() => {
                refetch();
            });
        } else {
            setPersonalDetailsErrors(errors);
            setSocialHandles(updatedLinks);
        }
    }, [
        personalDetails,
        personalDetailsHttpMethod,
        setDetails,
        setSocialLinks,
        socialHandles,
        socialHttpMethod,
        user_id,
    ]);

    useEffect(() => {
        if (personalDetailsResponse) {
            setPersonalDetails({
                first_name: personalDetailsResponse.data.attributes.first_name,
                last_name: personalDetailsResponse.data.attributes.last_name,
                gender: personalDetailsResponse.data.attributes.gender ?? "",
                nationality:
                    personalDetailsResponse.data.attributes.nationality ?? "",
                qualification:
                    personalDetailsResponse.data.attributes.qualification ?? "",
                dob: personalDetailsResponse.data.attributes.dob ?? "",
            });
            setPersonalDetailsHttpMethod("PUT");
        }
    }, [dispatch, personalDetailsResponse]);

    useEffect(() => {
        if (socialHandleData?.length) {
            setSocialHandles(socialHandleData);
            setSocialHttpMethod("PUT");
        }
    }, [socialHandleData, getSocialPlatformOptions]);

    useEffect(() => {
        if (updatePersonalDetailsResponse) {
            setPersonalDetailsHttpMethod("PUT");
        }
    }, [updatePersonalDetailsResponse]);

    useEffect(() => {
        if (updateSocialDetailsResponse) {
            setSocialHttpMethod("PUT");
        }
    }, [updateSocialDetailsResponse]);

    useEffect(() => {
        if (clickedOnNext && isSetDetailsSuccess && isSetSocialsSuccess) {
            navigateTo(
                routeConstants.profile.editProfile.root +
                    routeConstants.profile.editProfile.photosAndVideos
            );
        }
    }, [clickedOnNext, isSetDetailsSuccess, isSetSocialsSuccess, navigateTo]);

    useEffect(() => {
        if (isSetDetailsSuccess) {
            addToast({
                type: "success",
                text: "Personal details saved successfully!",
                timer: 3000,
            });
        }
    }, [isSetDetailsSuccess]);

    useEffect(() => {
        if (countries && countries.length > 0) {
            setNationalityData(
                countries.map((country) => ({
                    label: country.name,
                    value: country.name,
                }))
            );
        }
    }, [countries]);

    useEffect(() => {
        if (state && state.showToast) {
            addToast({
                text: "Please complete your profile details.",
                type: "error",
            });
            window.history.replaceState({}, "");
        }
    }, [state]);

    return {
        personalDetails,
        personalDetailsErrors,
        socialHandles,
        handleInputChange,
        addSocialHandle,
        removeSocialHandle,
        onPersonalDetailsSubmit,
        handleSelectChange,
        socialPlatformsOptions: getSocialPlatformOptions(socialPlatforms),
        handleSocialUsername,
        handleSocialSelect,
        handleSocialLinksSubmit,
        handleSocialEditButtonClick,
        isSocialLoading,
        isSocialFetching,
        handlePersonalDetailsEditButtonClick,
        isPersonalDetailLoading,
        isPersonalDetailsFetching,
        handleSaveAndNext,
        nationalityData,
        clickedOnNext,
    };
};
